import './LearningModule.scss';
import DOMPurify from 'dompurify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useServices } from 'index';
import { useParams, useSearchParams } from 'react-router-dom';
import Skeleton from '@trackman/web-shared-components/components/LoadingPlaceholder/Skeleton';
import { TableOfContents } from 'pages/TableOfContents/TableOfContents';
import { GetPremiumCTA } from 'components/PremiumCTA/GetPremiumCTA';
import { useState, useEffect } from 'react';
import { LearningModuleContentsEntry, LearningModuleLesson } from 'types/learningHub/moduleContents';
import { CalculatorKind } from 'types/calculator/calculatorType';
import { VideoLesson } from './TypeSpecificComponents/VideoLesson/VideoLesson';
import { CalculatorLesson } from './TypeSpecificComponents/CalculatorLesson/CalculatorLesson';
import { PictureLesson } from './TypeSpecificComponents/PictureLesson/PictureLesson';
import { TextLesson } from './TypeSpecificComponents/TextLesson/TextLesson';
import { ChapterTest } from './TypeSpecificComponents/ChapterTest/ChapterTest';
import { CertificationExam } from './TypeSpecificComponents/CertificationExam/CertificationExam';

import {
  getInitialSelectedEntryId,
  getSelectedEntry,
  isCertificationEntry,
  isChapterTestEntry,
  isLessonEntry,
} from 'utilities/learningModuleHelpers';
import { useIsUserMember } from 'queryHooks/permissions';
import { useGetModuleDetails, useGetModuleTableOfContents } from 'queryHooks/learningHub';
import { LearningModuleHeader } from './LearningModuleHeader/LearningModuleHeader';
import { ContentHeader } from './TypeSpecificComponents/ContentHeader/ContentHeader';

export const LearningModule = () => {
  const [searchParams] = useSearchParams();
  const [selectedEntryId, setSelectedEntryId] = useState<string | undefined>(searchParams.get('entryId') ?? undefined);

  const slug = useParams().slug ?? '';
  const service = useServices().learningHubService;
  const queryClient = useQueryClient();

  const { data: isUserMember, isPending } = useIsUserMember();
  const moduleDetails = useGetModuleDetails(slug);
  const tableOfContents = useGetModuleTableOfContents(moduleDetails.data?.id ?? '');

  const markResourceAsDone = useMutation({
    mutationFn: (contentId: string) => service.markResourceAsDone(contentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['moduleContents', moduleDetails.data?.id] });
    },
  });

  useEffect(() => {
    if (tableOfContents.data && !selectedEntryId) {
      const initialSelectedEntryId = getInitialSelectedEntryId(tableOfContents.data?.chapters ?? []);
      setSelectedEntryId(initialSelectedEntryId);
      const initialSelectedEntry = getSelectedEntry(
        tableOfContents.data?.chapters ?? [],
        tableOfContents.data?.certifications ?? [],
        initialSelectedEntryId
      );
      if (isLessonEntry(initialSelectedEntry) && 'isCompleted' in initialSelectedEntry && !initialSelectedEntry?.isCompleted) {
        markResourceAsDone.mutate(initialSelectedEntryId);
      }
    }
  }, [tableOfContents.data]);

  const selectEntry = (entryId: string, selectedEntry: LearningModuleContentsEntry) => {
    setSelectedEntryId(entryId);
    if (isLessonEntry(selectedEntry) && 'isCompleted' in selectedEntry && !selectedEntry?.isCompleted && !markResourceAsDone.isPending) {
      markResourceAsDone.mutate(entryId);
    }
  };

  const isViewLoaded = !moduleDetails.isPending && moduleDetails.data && !tableOfContents.isPending && tableOfContents.data;

  return (
    <>
      <LearningModuleHeader
        title={moduleDetails?.data?.title}
        shortDescription={moduleDetails?.data?.shortDescription}
        navigationPath={`/LearningHub/Modules/${slug}`}
        progress={tableOfContents?.data?.progress}
        isPending={moduleDetails.isPending && tableOfContents.isPending}
      ></LearningModuleHeader>

      <div className='learning-module-wrapper'>
        <div className='contents'>
          {isViewLoaded ? (
            <TableOfContents
              contents={tableOfContents?.data}
              isInteractive={true}
              customClasses='contents-in-module'
              selectedEntryId={selectedEntryId}
              handleEntrySelection={(id) =>
                selectEntry(id, getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], id))
              }
            />
          ) : (
            <Skeleton width='100%' height='100%' variant='rectangular' />
          )}
        </div>
        <div className='learning-module-content'>
          {isViewLoaded && selectedEntryId ? (
            <>
              <ContentHeader
                selectedEntry={getSelectedEntry(
                  tableOfContents.data?.chapters ?? [],
                  tableOfContents.data?.certifications ?? [],
                  selectedEntryId
                )}
                tableOfContents={tableOfContents.data}
                isUserMember={isUserMember}
                certificationTests={moduleDetails.data?.certificationTests}
                navigationDisabled={markResourceAsDone.isPending}
                handleSelectedEntry={(entryId) =>
                  selectEntry(
                    entryId,
                    getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], entryId)
                  )
                }
              />
              <div>
                {isChapterTestEntry(
                  getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                ) && (
                  <ChapterTest
                    chapterTestId={
                      getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                        ?.id ?? ''
                    }
                    moduleId={moduleDetails.data?.id ?? ''}
                    isCompleted={
                      getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                        ?.status === 'Completed'
                    }
                  />
                )}
                {isCertificationEntry(
                  getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                ) && (
                  <CertificationExam
                    isCompleted={
                      getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                        ?.status === 'Completed'
                    }
                    title={
                      getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                        ?.title ?? ''
                    }
                    certificationId={
                      getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                        ?.id ?? ''
                    }
                    moduleId={moduleDetails.data?.id ?? ''}
                    progress={
                      getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                        ?.progress ?? 0
                    }
                    badgeImageUrl={
                      moduleDetails.data?.certificationTests.find(
                        (ct) =>
                          ct.id ===
                          getSelectedEntry(
                            tableOfContents.data?.chapters ?? [],
                            tableOfContents.data?.certifications ?? [],
                            selectedEntryId
                          )?.id
                      )?.badgeImageUrl ?? ''
                    }
                  />
                )}
                {isLessonEntry(
                  getSelectedEntry(tableOfContents.data?.chapters ?? [], tableOfContents.data?.certifications ?? [], selectedEntryId)
                ) && (
                  <LessonComponent
                    content={
                      getSelectedEntry(
                        tableOfContents.data?.chapters ?? [],
                        tableOfContents.data?.certifications ?? [],
                        selectedEntryId
                      ) as LearningModuleLesson | undefined
                    }
                  />
                )}
              </div>
              {!isPending && !isUserMember && <GetPremiumCTA />}
            </>
          ) : (
            <>
              <Skeleton width={150} height={30} style={{ marginBottom: 20 }} variant='rounded' />
              <Skeleton width='100%' height='400px' variant='rounded' />
            </>
          )}
        </div>
      </div>
    </>
  );
};

type LessonProps = {
  content: LearningModuleLesson | undefined;
};

const LessonComponent = ({ content }: LessonProps) => {
  return (
    <>
      {content?.kind === 'Video' && <VideoLesson videoId={content.youTubeKey ?? ''} title={content.title} />}
      {content?.kind === 'Calculator' && (
        <CalculatorLesson
          calculatorKind={content.calculatorKind as CalculatorKind}
          calculatorOptions={JSON.parse(content.calculatorOptions ?? '')}
        />
      )}
      {content?.kind === 'Text' && <TextLesson text={content.text ?? ''} />}
      {content?.kind === 'Picture' && (
        <div className='picture-lesson'>
          <PictureLesson imageUrl={(content.medias && content.medias[0]?.fileUrl) ?? ''} imageTitle={content.title} />
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.text ?? '', { ADD_ATTR: ['target'] }) }}></div>
        </div>
      )}
    </>
  );
};
